/**
 * GamReportUpload — modal for uploading the month's GAM Direct Sold CSVs, the
 * data source that starts the Direct Sold pipeline. A month can have several
 * reports (the main network report plus reports for channels that need extra
 * filters); the server merges them into the one dataset DS processing reads.
 *
 * Flow: pick (drop zone / file picker, one or more CSVs) → preview (per-file
 * month + GAM report name read client-side, the month's existing reports, and
 * the add / replace-all choice) → uploading → done (server-confirmed stats).
 * Server detection is authoritative; the local pre-parse is a hint. Refusals
 * come back typed: a 422 overlap (a re-export offers "Replace X with Y"), the
 * 409 force guard once the pipeline progressed, and a 409 conflict when the
 * month's reports changed underneath.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Spinner, StatusPill } from '../ui';
import {
  uploadGamReports,
  getGamReportUpload,
  GamReportOverlap,
  GamReportSetMode,
  GamReportUploadError,
  GamReportUploadMeta,
  GamReportUploadResult,
  GamUploadRequiresForceError,
} from '../../services/adminApi';
import { GamCsvHint, readGamCsvHint } from './gamCsvPreamble';

/** The backend's per-request cap (gamReportSet MAX_FILES_PER_UPLOAD). */
const MAX_FILES = 5;

interface GamReportUploadProps {
  open: boolean;
  onClose: () => void;
  /** Month pre-selected by the pipeline UI ("YYYY-MM") — the fallback when detection fails */
  defaultMonth?: string;
  /** True when the month's pipeline already progressed past the upload — show staleness warning */
  pipelineInProgress?: boolean;
  /** Files already picked outside the modal (the report card's drop zone) — skips the pick phase */
  initialFiles?: File[] | null;
  /** The card button that opened the modal; unset → decided from the month's reports */
  intent?: 'add' | 'replace-all';
  onUploaded: (result: GamReportUploadResult) => void;
}

type Phase = 'pick' | 'preview' | 'uploading' | 'done';
type SetChoice = 'add' | 'replace-all';

interface PickedFile {
  key: string;
  file: File;
  hint: GamCsvHint;
}

interface PendingChange {
  mode: GamReportSetMode;
  replaceSourceIds?: string[];
}

interface UploadFailure {
  message: string;
  code?: string;
  overlaps: GamReportOverlap[];
  /** The change a force confirmation repeats */
  change: PendingChange;
}

/** The month the existing-report panel describes (null until loaded). */
interface MonthReports {
  month: string;
  upload: GamReportUploadMeta | null;
  expected: number;
}

/** "2026-06" → "June 2026" */
function monthLabel(month: string): string {
  const [y, m] = month.split('-').map((v) => parseInt(v, 10));
  if (!y || !m) return month;
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

const fileKey = (f: File): string => `${f.name}|${f.size}|${f.lastModified}`;
const fmtUsd = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtSize = (bytes: number): string =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(2)} MB`;
const plural = (n: number, word: string): string => `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;

/**
 * The one-click fix for a refused add: every overlap pairs a kept report with
 * a new file that re-exports it, so replacing the kept ones resolves them all.
 */
function reexportReplacement(
  overlaps: readonly GamReportOverlap[]
): { replaceSourceIds: string[]; keptNames: string[]; newNames: string[] } | null {
  if (overlaps.length === 0) return null;
  if (!overlaps.every((o) => o.likelyReexport && o.a.isNew !== o.b.isNew)) return null;
  const kept = overlaps.map((o) => (o.a.isNew ? o.b : o.a));
  const fresh = overlaps.map((o) => (o.a.isNew ? o.a : o.b));
  const unique = (list: string[]) => Array.from(new Set(list));
  return {
    replaceSourceIds: unique(kept.map((k) => k.sourceId)),
    keptNames: unique(kept.map((k) => k.fileName)),
    newNames: unique(fresh.map((f) => f.fileName)),
  };
}

const GamReportUpload: React.FC<GamReportUploadProps> = ({
  open,
  onClose,
  defaultMonth,
  pipelineInProgress,
  initialFiles,
  intent,
  onUploaded,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('pick');
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [monthOverride, setMonthOverride] = useState('');
  const [monthReports, setMonthReports] = useState<MonthReports | null>(null);
  // The month whose reports finished loading: the add/replace default depends
  // on them, so Upload waits for the month it would upload to
  const [checkedMonth, setCheckedMonth] = useState<string | null>(null);
  const [choice, setChoice] = useState<SetChoice | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [failure, setFailure] = useState<UploadFailure | null>(null);
  const [result, setResult] = useState<GamReportUploadResult | null>(null);

  const detectedMonths = useMemo(
    () => Array.from(new Set(files.map((f) => f.hint.month).filter((m): m is string => Boolean(m)))),
    [files]
  );
  const detectedMonth = detectedMonths.length === 1 ? detectedMonths[0] : null;
  const mixedMonths = !monthOverride && detectedMonths.length > 1;
  const effectiveMonth = monthOverride || detectedMonth || defaultMonth || '';

  // The existing-report panel only ever describes the month being uploaded to
  const current = monthReports && monthReports.month === effectiveMonth ? monthReports : null;
  const existing = current?.upload ?? null;
  const existingSources = existing?.sources ?? [];
  const existingCount = existing ? Math.max(existingSources.length, 1) : 0;
  const expected = current?.expected ?? 1;
  const defaultChoice: SetChoice = intent ?? (existingCount > 1 || expected > 1 ? 'add' : 'replace-all');
  const setChoiceValue: SetChoice = choice ?? defaultChoice;
  const mode: SetChoice = existing ? setChoiceValue : 'add';

  // Reset on open; the report card can hand files straight in, which skips
  // the pick phase and lands on the preview (all warning/409 logic intact).
  useEffect(() => {
    if (open) {
      setPhase('pick');
      setFiles([]);
      setMonthOverride('');
      setMonthReports(null);
      setCheckedMonth(null);
      setChoice(null);
      setPickError(null);
      setFailure(null);
      setResult(null);
      if (initialFiles?.length) void addFiles(initialFiles, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFiles]);

  // The month's current reports, whenever the preview's month changes (and
  // after a refused upload, so a conflict shows what changed).
  useEffect(() => {
    let stale = false;
    if (open && effectiveMonth && phase === 'preview') {
      setCheckedMonth(null);
      getGamReportUpload(effectiveMonth)
        .then((r) => {
          if (!stale) setMonthReports({ month: effectiveMonth, upload: r.upload, expected: r.expectedReports ?? 1 });
        })
        .catch(() => {
          if (!stale) setMonthReports(null); // unknown: the server still checks everything
        })
        .finally(() => {
          if (!stale) setCheckedMonth(effectiveMonth);
        });
    }
    return () => {
      stale = true;
    };
  }, [open, effectiveMonth, phase]);

  const addFiles = async (incoming: File[], already: PickedFile[] = files) => {
    const csvs = incoming.filter((f) => f.name.toLowerCase().endsWith('.csv'));
    const skipped = incoming.length - csvs.length;
    const seen = new Set(already.map((f) => f.key));
    const fresh = csvs.filter((f) => {
      const key = fileKey(f);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (csvs.length === 0) {
      setPickError('The GAM report must be a .csv export');
      return;
    }
    if (already.length + fresh.length > MAX_FILES) {
      setPickError(`Upload at most ${MAX_FILES} GAM reports at a time.`);
      return;
    }
    const hints = await Promise.all(fresh.map((f) => readGamCsvHint(f)));
    setFiles([...already, ...fresh.map((file, i) => ({ key: fileKey(file), file, hint: hints[i] }))]);
    setPickError(skipped > 0 ? `${plural(skipped, 'file')} skipped — only .csv exports can be uploaded.` : null);
    setFailure(null);
    setPhase('preview');
  };

  const removeFile = (key: string) => {
    const next = files.filter((f) => f.key !== key);
    setFiles(next);
    setFailure(null);
    if (next.length === 0) setPhase('pick');
  };

  const submit = async (change: PendingChange, force = false) => {
    if (files.length === 0) return;
    setPhase('uploading');
    setFailure(null);
    try {
      const res = await uploadGamReports(
        files.map((f) => f.file),
        {
          ...change,
          monthOverride: monthOverride || undefined,
          force,
          // What the preview showed: a change underneath since then is a 409
          // conflict. Only when the month is known — a fallback month the
          // server may not detect would conflict for the wrong month.
          baseRevision:
            current && (monthOverride || detectedMonth) ? (current.upload ? current.upload.revision : 0) : undefined,
        }
      );
      setResult(res);
      setPhase('done');
      onUploaded(res); // fire immediately so the pipeline bar refreshes behind the modal
    } catch (err: any) {
      setFailure({
        message: err?.message || 'Upload failed',
        code: err instanceof GamUploadRequiresForceError ? 'requires_force' : err instanceof GamReportUploadError ? err.code : undefined,
        overlaps: err instanceof GamReportUploadError ? err.overlaps : [],
        change,
      });
      setPhase('preview');
    }
  };

  const startAnother = () => {
    setFiles([]);
    setMonthOverride('');
    setChoice('add');
    setPickError(null);
    setFailure(null);
    setResult(null);
    setPhase('pick');
  };

  const needsForce = failure?.code === 'requires_force';
  const replacement = failure ? reexportReplacement(failure.overlaps) : null;
  const monthDisplay = effectiveMonth ? monthLabel(effectiveMonth) : '';

  const warnings = useMemo(() => {
    const list: string[] = [];
    if (phase !== 'preview') return list;
    if (mixedMonths) {
      list.push(
        'These files cover different months (' +
          files.map((f) => `${f.file.name} → ${f.hint.month ? monthLabel(f.hint.month) : 'unknown'}`).join('; ') +
          ') — upload one month at a time.'
      );
    } else if (!detectedMonth && !monthOverride) {
      list.push(`Could not detect the month from the ${files.length > 1 ? 'files' : 'file'} — pick it below.`);
    }
    for (const f of files) {
      if (f.hint.partial && f.hint.month) {
        list.push(
          (files.length > 1 ? `${f.file.name}: the` : 'The') +
            ' Date range does not span a full month — this looks like a partial-month report.'
        );
      }
    }
    if (existing && mode === 'replace-all') {
      list.push(
        existingCount === 1
          ? `Replaces the ${monthLabel(existing.month)} report uploaded ` +
              `${new Date(existing.uploadedAt).toLocaleDateString()} by ${existing.uploadedBy}.`
          : `Replaces all ${existingCount} ${monthLabel(existing.month)} reports (last changed ` +
              `${new Date(existing.uploadedAt).toLocaleDateString()} by ${existing.uploadedBy}).`
      );
    }
    if (pipelineInProgress) {
      list.push(
        mode === 'add' && existing
          ? 'This month’s pipeline already ran — re-run the affected steps after adding the report.'
          : 'This month’s pipeline already ran — re-run the affected steps after replacing the report.'
      );
    }
    return list;
  }, [phase, mixedMonths, files, detectedMonth, monthOverride, existing, existingCount, mode, pipelineInProgress]);

  const uploadLabel = (() => {
    if (!existing) return files.length > 1 ? `Upload ${files.length} reports` : 'Upload';
    if (mode === 'add') return files.length > 1 ? `Add ${files.length} reports` : 'Add report';
    return 'Upload & Replace';
  })();
  const canUpload =
    Boolean(effectiveMonth) && !mixedMonths && files.length > 0 && checkedMonth === effectiveMonth;

  const resultSources = result?.upload.sources ?? [];
  const resultCount = result ? Math.max(resultSources.length, 1) : 0;
  const resultExpected = result && current && current.month === result.upload.month ? current.expected : 1;
  const resultMissing = Math.max(0, resultExpected - resultCount);

  const footer = (() => {
    if (phase === 'pick') {
      return <Button variant="ghost" onClick={onClose}>Cancel</Button>;
    }
    if (phase === 'preview') {
      return (
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {needsForce && failure ? (
            <Button variant="destructive" onClick={() => submit(failure.change, true)} disabled={!canUpload}>
              {failure.change.mode === 'add' ? 'Add & Continue' : 'Replace & Continue'}
            </Button>
          ) : (
            <Button variant="primary" onClick={() => submit({ mode })} disabled={!canUpload}>
              {uploadLabel}
            </Button>
          )}
        </>
      );
    }
    if (phase === 'done') {
      return (
        <>
          {resultMissing > 0 && (
            <Button variant="ghost" onClick={startAnother}>Add another report</Button>
          )}
          <Button variant="primary" onClick={onClose}>Done</Button>
        </>
      );
    }
    return null;
  })();

  return (
    <Modal
      open={open}
      onClose={phase === 'uploading' ? () => {} : onClose}
      title="Upload GAM Direct Sold Reports"
      eyebrow="Monthly Processing"
      footer={footer}
      width={560}
      closeOnEsc={phase !== 'uploading'}
      closeOnBackdrop={false}
    >
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        multiple
        data-testid="gam-report-file-input"
        style={{ display: 'none' }}
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          if (picked.length) void addFiles(picked);
          e.target.value = '';
        }}
      />

      {phase === 'pick' && (
        <div>
          <div
            style={{ ...styles.dropZone, ...(dragOver ? styles.dropZoneActive : {}) }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const dropped = Array.from(e.dataTransfer.files ?? []);
              if (dropped.length) void addFiles(dropped);
            }}
            onClick={() => fileRef.current?.click()}
          >
            <div style={styles.dropTitle}>Drop the GAM CSVs here</div>
            <div style={styles.dropHint}>
              or click to choose the &quot;Ad Impressions &amp; Revenue by Channel [Monthly]&quot; export —
              one file or all of the month&rsquo;s reports at once
            </div>
          </div>
          {pickError && <div style={styles.error}>{pickError}</div>}
        </div>
      )}

      {phase === 'preview' && files.length > 0 && (
        <div>
          <div style={styles.fileList}>
            {files.map((f) => (
              <div key={f.key} style={styles.fileRow}>
                <div style={styles.fileMain}>
                  <div style={styles.fileTop}>
                    <span style={styles.fileName} title={f.file.name}>{f.file.name}</span>
                    <span style={styles.fileSize}>{fmtSize(f.file.size)}</span>
                  </div>
                  <div style={styles.fileHint} title={f.hint.filtersText ? `Filters: ${f.hint.filtersText}` : undefined}>
                    {[f.hint.month ? monthLabel(f.hint.month) : 'month not detected', f.hint.reportName]
                      .filter(Boolean)
                      .join(' · ')}
                    {f.hint.filtersText ? ' · filtered' : ''}
                  </div>
                </div>
                <button
                  type="button"
                  style={styles.removeFile}
                  onClick={() => removeFile(f.key)}
                  aria-label={`Remove ${f.file.name}`}
                  title="Remove this file"
                >
                  ×
                </button>
              </div>
            ))}
            {files.length < MAX_FILES && (
              <button type="button" style={styles.addFile} onClick={() => fileRef.current?.click()}>
                + Add another file
              </button>
            )}
          </div>

          <div style={styles.fieldRow}>
            <label style={styles.label} htmlFor="gam-report-month">Report month</label>
            <input
              id="gam-report-month"
              type="month"
              value={effectiveMonth}
              onChange={(e) => {
                setMonthOverride(e.target.value);
                setFailure(null);
              }}
              style={styles.monthInput}
            />
            {detectedMonth && (
              <span style={styles.detectedNote}>
                {monthOverride && monthOverride !== detectedMonth
                  ? `detected: ${monthLabel(detectedMonth)}`
                  : 'detected from Date range'}
              </span>
            )}
          </div>

          {existing && (
            <div style={styles.existingBox}>
              <div style={styles.existingTitle}>
                {monthDisplay} already has {existingCount === 1 ? 'a GAM report' : `${existingCount} GAM reports`}
                {expected > 1 ? ` (${expected} expected)` : ''}
              </div>
              {(existingSources.length ? existingSources : [existing]).map((s) => (
                <div key={'sourceId' in s ? s.sourceId : s.fileName} style={styles.existingRow}>
                  <span style={styles.existingName} title={s.fileName}>{s.fileName}</span>
                  <span style={styles.existingStats}>
                    {s.rowCount.toLocaleString()} rows · {fmtUsd(s.totalRevenue)}
                  </span>
                </div>
              ))}
              <div style={styles.choiceGroup} role="radiogroup" aria-label="What to do with the existing reports">
                <label style={styles.choice}>
                  <input
                    type="radio"
                    name="gam-report-set-choice"
                    checked={setChoiceValue === 'add'}
                    onChange={() => { setChoice('add'); setFailure(null); }}
                  />
                  <span>
                    <span style={styles.choiceTitle}>Add to the month&rsquo;s reports</span>
                    <span style={styles.choiceHint}>
                      Merged with {existingCount === 1 ? 'the report' : 'the reports'} above into one dataset for DS processing.
                    </span>
                  </span>
                </label>
                <label style={styles.choice}>
                  <input
                    type="radio"
                    name="gam-report-set-choice"
                    checked={setChoiceValue === 'replace-all'}
                    onChange={() => { setChoice('replace-all'); setFailure(null); }}
                  />
                  <span>
                    <span style={styles.choiceTitle}>Replace all</span>
                    <span style={styles.choiceHint}>
                      Removes {existingCount === 1 ? 'the report' : 'the reports'} above — DS processing reads only the new{' '}
                      {files.length > 1 ? 'files' : 'file'}.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}

          {warnings.length > 0 && (
            <div style={styles.warningBox}>
              {warnings.map((w, i) => (
                <div key={i} style={styles.warningItem}>⚠ {w}</div>
              ))}
            </div>
          )}
          {pickError && <div style={styles.error}>{pickError}</div>}
          {failure && (
            <div style={styles.failureBox} role="alert">
              <div style={styles.error}>{failure.message}</div>
              {failure.overlaps.map((o, i) => (
                <div key={i} style={styles.overlapRow}>
                  {failure.overlaps.length > 1 ? `${o.b.fileName} ↔ ${o.a.fileName}: ` : ''}
                  {plural(o.rowCount, 'shared ad row')} ({fmtUsd(o.revenue)}) on {plural(o.channelCount, 'channel')}
                  {o.channels.length > 0 &&
                    `, e.g. ${o.channels.slice(0, 5).join(', ')}${o.channelCount > Math.min(o.channels.length, 5) ? ', …' : ''}`}
                </div>
              ))}
              {replacement && !needsForce && (
                <Button
                  variant="primary"
                  size="sm"
                  style={{ marginTop: 8, alignSelf: 'flex-start' }}
                  title={`Replace ${replacement.keptNames.join(' and ')} with ${replacement.newNames.join(' and ')}`}
                  onClick={() => submit({ mode: 'replace', replaceSourceIds: replacement.replaceSourceIds })}
                >
                  {replacement.keptNames.length === 1 ? 'Replace the older report' : 'Replace the older reports'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {phase === 'uploading' && (
        <div style={styles.centered}>
          <Spinner
            size={28}
            label={files.length > 1 ? `Uploading & merging ${files.length} reports…` : `Uploading & parsing ${files[0]?.file.name || 'report'}…`}
          />
        </div>
      )}

      {phase === 'done' && result && (
        <div>
          <div style={styles.doneHeader}>
            <StatusPill variant="ok">Uploaded</StatusPill>
            {resultCount > 1 && <StatusPill variant="approved">{resultCount} reports merged</StatusPill>}
            {result.replacedExisting && <StatusPill variant="pending">Replaced previous</StatusPill>}
          </div>
          <div style={styles.statGrid}>
            <div style={styles.stat}><span style={styles.statLabel}>Month</span><span style={styles.statValue}>{result.upload.monthDisplay}</span></div>
            <div style={styles.stat}><span style={styles.statLabel}>Rows</span><span style={styles.statValue}>{result.upload.rowCount.toLocaleString()}</span></div>
            <div style={styles.stat}><span style={styles.statLabel}>Channels</span><span style={styles.statValue}>{result.upload.channelCount.toLocaleString()}</span></div>
            <div style={styles.stat}><span style={styles.statLabel}>Impressions</span><span style={styles.statValue}>{result.upload.totalImpressions.toLocaleString()}</span></div>
            <div style={styles.stat}><span style={styles.statLabel}>Revenue</span><span style={styles.statValue}>${result.upload.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
          </div>
          {resultSources.length > 1 && (
            <div style={styles.sourceTable}>
              {resultSources.map((s) => (
                <div key={s.sourceId} style={styles.existingRow}>
                  <span style={styles.existingName} title={s.fileName}>{s.fileName}</span>
                  <span style={styles.existingStats}>
                    {s.rowCount.toLocaleString()} rows · {fmtUsd(s.totalRevenue)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {result.upload.parseWarnings.length > 0 && (
            <div style={styles.warningBox}>
              {result.upload.parseWarnings.map((w, i) => (
                <div key={i} style={styles.warningItem}>⚠ {w}</div>
              ))}
            </div>
          )}
          <div style={styles.doneHint}>
            {resultMissing > 0
              ? `${resultCount} of ${resultExpected} GAM reports uploaded for ${result.upload.monthDisplay} — ` +
                `add the remaining ${resultMissing === 1 ? 'report' : 'reports'} before starting processing.`
              : `The pipeline can now start for ${result.upload.monthDisplay} from the Pipeline tab.`}
          </div>
        </div>
      )}
    </Modal>
  );
};

const styles: Record<string, React.CSSProperties> = {
  dropZone: {
    border: '2px dashed var(--border-1)',
    borderRadius: 'var(--radius-md, 8px)',
    padding: '32px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    background: 'var(--bg-surface-3)',
    transition: 'border-color 120ms ease, background 120ms ease',
  },
  dropZoneActive: {
    borderColor: 'var(--accent)',
    background: 'var(--accent-soft)',
  },
  dropTitle: { fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' },
  dropHint: { fontSize: 12, color: 'var(--fg-3)', marginTop: 6, lineHeight: 1.45 },
  fileList: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    background: 'var(--bg-surface-3)',
    borderRadius: 6,
  },
  fileMain: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 },
  fileTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  fileName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--fg-1)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileSize: { fontSize: 12, color: 'var(--fg-3)', flexShrink: 0 },
  fileHint: {
    fontSize: 11.5,
    color: 'var(--fg-3)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeFile: {
    border: 'none',
    background: 'transparent',
    color: 'var(--fg-3)',
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
    padding: '2px 6px',
    flexShrink: 0,
  },
  addFile: {
    alignSelf: 'flex-start',
    border: 'none',
    background: 'transparent',
    color: 'var(--accent)',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '2px 0',
  },
  fieldRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  label: { fontSize: 12, fontWeight: 600, color: 'var(--fg-2)' },
  monthInput: {
    padding: '6px 8px',
    border: '1px solid var(--border-1)',
    borderRadius: 6,
    background: 'var(--bg-surface-1)',
    color: 'var(--fg-1)',
    fontSize: 13,
  },
  detectedNote: { fontSize: 11, color: 'var(--fg-3)' },
  existingBox: {
    border: '1px solid var(--border-1)',
    borderRadius: 6,
    padding: '10px 12px',
    marginBottom: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  existingTitle: { fontSize: 12.5, fontWeight: 600, color: 'var(--fg-1)' },
  existingRow: { display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, minWidth: 0 },
  existingName: { color: 'var(--fg-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  existingStats: { color: 'var(--fg-3)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' },
  choiceGroup: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 },
  choice: { display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' },
  choiceTitle: { display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--fg-1)' },
  choiceHint: { display: 'block', fontSize: 11.5, color: 'var(--fg-3)', lineHeight: 1.4 },
  warningBox: {
    background: 'var(--bg-surface-3)',
    border: '1px solid var(--border-1)',
    borderRadius: 6,
    padding: '8px 10px',
    marginBottom: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  warningItem: { fontSize: 12, color: 'var(--warning)', lineHeight: 1.4 },
  failureBox: { display: 'flex', flexDirection: 'column', gap: 4 },
  overlapRow: { fontSize: 11.5, color: 'var(--fg-2)', lineHeight: 1.4 },
  error: { fontSize: 12, color: 'var(--danger)', marginTop: 8, lineHeight: 1.4 },
  centered: { display: 'flex', justifyContent: 'center', padding: '28px 0' },
  doneHeader: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  statGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '8px 10px',
    background: 'var(--bg-surface-3)',
    borderRadius: 6,
  },
  statLabel: { fontSize: 11, color: 'var(--fg-3)', fontWeight: 600, textTransform: 'uppercase' as const },
  statValue: { fontSize: 14, fontWeight: 700, color: 'var(--fg-1)' },
  sourceTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '8px 10px',
    border: '1px solid var(--border-1)',
    borderRadius: 6,
    marginBottom: 12,
  },
  doneHint: { fontSize: 12, color: 'var(--fg-3)' },
};

export default GamReportUpload;
